import { useId } from 'react';

import { Stack } from 'ui/stack';

import Badge from 'components/partials/badge/badge';
import Button from 'components/partials/button/button';
import Drawer from 'components/partials/drawer/drawer';
import Input from 'components/partials/input/input';
import { TextArea } from 'ui/text-area';

import DimensionBlock from 'containers/admin/clients/client/tabs-components/critical-populations/components/dimension-block';
import MonitoringConfirmModal from 'containers/admin/clients/client/tabs-components/critical-populations/components/monitoring-confirm-modal';
import OriginBadge from 'containers/admin/clients/client/tabs-components/critical-populations/components/origin-badge';
import {
  CREATE_NOTE_HELPER,
  EDIT_STATUS_BADGE,
  MAX_DIMENSIONS,
  NOTE_MAX_LENGTH,
  NOTE_TEXTAREA_ROWS,
  TITLE_PREVIEW_PLACEHOLDER,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type { FormDrawerMode } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { useCaptureForm } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-capture-form';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import Icons from 'assets/icons';

interface FormDrawerProps {
  open: boolean;
  onExited: () => void;
  mode: FormDrawerMode;
  row?: CriticalPopulationResponse;
  clientId: number;
  enteringClassYear: number;
  isMonitoringPending?: boolean;
  onClose: () => void;
}

const FormDrawer = ({
  open,
  onExited,
  mode,
  row,
  clientId,
  enteringClassYear,
  isMonitoringPending = false,
  onClose,
}: FormDrawerProps) => {
  const noteId = useId();
  const titleId = useId();
  const form = useCaptureForm({
    clientId,
    mode,
    row,
    enteringClassYear,
    isMonitoringPending,
    onClose,
  });
  const isCreate = mode === 'create';
  const statusBadge = row?.status ? EDIT_STATUS_BADGE[row.status] : undefined;

  return (
    <>
      <Drawer
        open={open}
        onExited={onExited}
        onClose={onClose}
        title={
          isCreate
            ? 'Add custom Critical Element'
            : row?.summary ?? 'Edit Critical Element'
        }
        headerSlot={
          <Stack direction="row" sx={{ gap: 1 }}>
            {isCreate ? (
              <Badge text="Custom" variant="reserve-outlined" />
            ) : (
              <>
                {statusBadge && (
                  <Badge
                    text={statusBadge.text}
                    variant={statusBadge.variant}
                    showStatusDot
                  />
                )}
                <OriginBadge origin={row?.origin} />
              </>
            )}
          </Stack>
        }
        footer={
          <>
            <Button
              variant="brand-filled"
              size="md"
              disabled={form.isSubmitDisabled}
              loading={form.isPending}
              onClick={form.handleSubmit}
              data-testid="form-drawer-submit"
            >
              {isCreate ? 'Promote' : 'Save changes'}
            </Button>
            <Button
              variant="neutral-outlined"
              size="md"
              onClick={onClose}
              data-testid="form-drawer-cancel"
            >
              Cancel
            </Button>
          </>
        }
        data-testid="form-drawer"
      >
        <Stack sx={{ gap: 0.5 }}>
          <Input
            label="Critical Element name"
            labelFor={titleId}
            labelInTop
            customClass="text-base leading-6"
            required
            placeholder={TITLE_PREVIEW_PLACEHOLDER}
            value={form.titleValue}
            onChange={(event) => form.handleTitleChange(event.target.value)}
            errorsNames={
              form.previewError || form.titleError
                ? { message: form.previewError ?? form.titleError }
                : undefined
            }
            data-testid="form-drawer-title"
          />
          {form.canRestoreTitle && (
            <Stack direction="row">
              <Button
                variant="reserve-text-link"
                size="sm"
                onClick={form.handleRestoreTitle}
                data-testid="form-drawer-restore-title"
              >
                Use generated name
              </Button>
            </Stack>
          )}
        </Stack>

        <Stack sx={{ gap: 1.5 }}>
          <Stack sx={{ gap: '1px' }}>
            <h3 className="text-base font-bold text-gray-700">Dimensions</h3>
            <p className="text-sm text-gray-700">
              Select up to {MAX_DIMENSIONS} dimensions. Each dimension can be
              used once.
            </p>
          </Stack>

          {form.isCatalogLoading && (
            <p
              className="text-sm text-gray-500"
              data-testid="form-drawer-loading"
            >
              Loading dimensions...
            </p>
          )}

          {form.isCatalogError && (
            <Stack role="alert" sx={{ alignItems: 'flex-start', gap: 1 }}>
              <p className="text-sm text-gray-700">
                Couldn&apos;t load dimensions.
              </p>
              <Button
                variant="neutral-outlined"
                size="sm"
                onClick={form.retryCatalog}
              >
                Retry
              </Button>
            </Stack>
          )}

          {form.blocks.map((block, index) => (
            <DimensionBlock
              key={block.key}
              block={block}
              index={index}
              canRemove={form.canRemoveDimension}
              onAttributeChange={form.handleAttributeChange}
              onValuesChange={form.handleValuesChange}
              onRemove={form.handleRemoveDimension}
            />
          ))}

          {form.canAddDimension && (
            <Stack direction="row">
              <Button
                variant="reserve-outlined"
                size="sm"
                iconBefore={<Icons.PlusThin className="size-4" aria-hidden="true" />}
                onClick={form.handleAddDimension}
                data-testid="form-drawer-add-dimension"
              >
                Add dimension
              </Button>
            </Stack>
          )}
        </Stack>

        <TextArea
          label="Reason"
          id={noteId}
          required
          helperText={isCreate ? CREATE_NOTE_HELPER : undefined}
          maxLength={NOTE_MAX_LENGTH}
          rows={NOTE_TEXTAREA_ROWS}
          value={form.noteValue}
          onChange={form.handleNoteChange}
          errorMessage={form.noteError}
          data-testid="form-drawer-note"
        />
      </Drawer>
      {form.isConfirmOpen && (
        <MonitoringConfirmModal
          onConfirm={form.handleConfirmSave}
          onCancel={form.handleCancelConfirm}
        />
      )}
    </>
  );
};

export default FormDrawer;
